import { h, mount, clear } from "@/lib/dom";
import { icon } from "@/lib/icons";
import { topicsService, pbErrorMessage } from "@/services";
import { topicSources, type TopicSources } from "@/services/source.service";
import { transcribe, speak, aiAvailableSync } from "@/services/ai.service";
import { markPracticed } from "@/services/streak.service";
import { speechService } from "@/services/speech-rounds.service";
import { createRecorder, micSupported } from "@/features/vortrag/recorder";
import { aiTroubleCard } from "@/features/learn/aiHealth";
import { pfRoot, pfHead, pfLoading, pfButton, pfBig, pfEmpty, onEnter } from "@/features/pfad/shell";
import { navigate, type RouteContext } from "@/app/router";
import { CHARACTER_LIST, CHARACTERS, type Character } from "@/features/gespraech/characters";
import { nextTurn, type DialogueTurn, type Mood } from "@/features/gespraech/dialogue";
import type { Topic } from "@/types/models";

/**
 * Gespräch: ein Charakter stellt Fragen zum Thema, hört sich die gesprochene
 * Antwort an und reagiert – Ton und Laune passen sich an die Qualität der
 * Antwort an. Turn-Taking ist strikt: erst hört man den Partner zu Ende
 * (Audio läuft ab, Mikro bleibt gesperrt), dann nimmt man auf und schickt
 * die Antwort über einen eigenen Knopf ab (kein automatisches Abschicken
 * bei Stille).
 */
export async function GespraechPage(ctx: RouteContext): Promise<HTMLElement> {
  const topicId = ctx.params.id;
  const toTopic = () => navigate(`/topic/${topicId}/learn`);

  const head = pfHead(toTopic, { streak: false });
  const stage = h("div", { class: "pf-stage" });
  const foot = h("div", { class: "pf-foot" });
  const root = pfRoot(head.el, stage, foot);
  clear(foot);

  mount(stage, h("div", { class: "pf-body" }, pfLoading("Material wird gelesen")));

  let topic: Topic;
  let src: TopicSources;
  try {
    topic = await topicsService.get(topicId);
    src = await topicSources(topic);
  } catch (err) {
    mount(stage, h("div", { class: "pf-body" }, pfEmpty("Thema nicht ladbar", pbErrorMessage(err), pfButton("Zurück", toTopic, "gray"))));
    return root;
  }

  if (!aiAvailableSync()) {
    mount(stage, h("div", { class: "pf-body" }, aiTroubleCard("Gespräch braucht KI für Sprachausgabe, Erkennung und Antworten.")));
    return root;
  }
  if (!micSupported()) {
    mount(
      stage,
      h(
        "div",
        { class: "pf-body" },
        pfEmpty(
          "Kein Mikrofon verfügbar",
          "Dieser Browser gibt keine Aufnahme frei. In der App oder in Chrome/Safari klappt es.",
          pfButton("Zurück", toTopic, "gray")
        )
      )
    );
    return root;
  }

  let character: Character = CHARACTERS.kid;
  const history: DialogueTurn[] = [];
  let round = 1;

  renderSetup();
  return root;

  /* ----------------------------- Einstellungen ----------------------------- */

  function renderSetup(): void {
    head.set(0, 0, 1);
    clear(head.right);

    const cards = CHARACTER_LIST.map((c) =>
      h(
        "button",
        {
          type: "button",
          class: "gp-char" + (c.key === character.key ? " on" : ""),
          onclick: () => {
            character = c;
            cards.forEach((el, i) => el.classList.toggle("on", CHARACTER_LIST[i].key === c.key));
          },
        },
        h("div", { class: "gp-char-title" }, c.label),
        h("div", { class: "gp-char-desc" }, c.description)
      )
    );

    mount(
      stage,
      h(
        "div",
        { class: "pf-body" },
        h(
          "div",
          { class: "pf-setup" },
          h("h1", null, "Gespräch"),
          h("p", { class: "lead" }, `Sprich mit jemandem über „${topic.title}" – wer stellt die Fragen?`),
          h("div", { class: "gp-char-grid" }, ...cards)
        )
      )
    );
    onEnter(root, () => void startRound());
    mount(foot, h("div", { class: "pf-foot-in" }, pfBig("Los geht's", () => void startRound())));
  }

  /* ------------------------------- Gespräch -------------------------------- */

  async function startRound(): Promise<void> {
    history.length = 0;
    round = 1;
    await advance();
  }

  /** Holt den nächsten Partner-Beitrag, spricht ihn (TTS), dann Mikro frei. */
  async function advance(): Promise<void> {
    head.set(round, 0, Math.max(round, 4));
    mount(stage, h("div", { class: "pf-body" }, pfLoading(`${character.label} überlegt …`)));
    clear(head.right);
    try {
      const turn = await nextTurn({ character, topicTitle: topic.title, sourcesText: src.text, history });
      history.push({ role: "partner", text: turn.text });
      renderSpeaking(turn.text, turn.mood);
      let audio: Blob | null = null;
      try {
        audio = await speak(turn.text);
      } catch {
        // Sprachausgabe optional: ohne Audio geht es mit Text sofort weiter.
      }
      if (audio) await playAndWait(audio);
      if (turn.done || round >= 8) {
        void finish();
        return;
      }
      renderListening();
    } catch (err) {
      mount(
        stage,
        h(
          "div",
          { class: "pf-body" },
          aiTroubleCard(pbErrorMessage(err)),
          h(
            "div",
            { style: "margin-top:14px;display:flex;gap:10px;justify-content:center" },
            pfButton("Nochmal versuchen", () => void advance(), "blue"),
            pfButton("Beenden", () => void finish(), "ghost")
          )
        )
      );
    }
  }

  function playAndWait(blob: Blob): Promise<void> {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(blob);
      const audioEl = new Audio(url);
      const done = () => {
        URL.revokeObjectURL(url);
        resolve();
      };
      audioEl.addEventListener("ended", done, { once: true });
      audioEl.addEventListener("error", done, { once: true });
      void audioEl.play().catch(done);
    });
  }

  const MOOD_LABEL: Record<Mood, string> = {
    neutral: "…",
    begeistert: "Klingt begeistert!",
    genervt: "Klingt etwas genervt.",
    amuesiert: "Klingt amüsiert.",
    traurig: "Klingt enttäuscht.",
    wuetend: "Klingt aufgebracht.",
    beeindruckt: "Klingt beeindruckt!",
  };

  function renderSpeaking(text: string, mood: Mood): void {
    mount(
      stage,
      h(
        "div",
        { class: "pf-body" },
        h(
          "div",
          { class: "vt-stage gp-speaking" },
          h("p", { class: "muted", style: "font-size:13px;margin:0" }, `${character.label} · Runde ${round}`),
          h("div", { class: "gp-avatar" }, h("span", { class: "row", html: icon("mic", 34) })),
          h("p", { class: "gp-mood" }, MOOD_LABEL[mood] ?? ""),
          h("h2", { class: "pf-q-title", style: "text-align:center" }, text)
        )
      )
    );
    clear(foot);
  }

  function renderListening(): void {
    const recorder = createRecorder();
    let raf = 0;
    let recording = false;
    let transcript = "";

    const pulse = h("div", { class: "vt-pulse" });
    const micBtn = h(
      "button",
      { class: "vt-mic", type: "button", title: "Antwort aufnehmen" },
      h("span", { class: "row", html: icon("mic", 42) })
    ) as HTMLButtonElement;
    const status = h("p", { class: "vt-status" }, "Tippe auf das Mikrofon und antworte.");
    const transcriptBox = h("div", { class: "gp-transcript", style: "display:none" });
    const sendBtn = pfButton("Antwort abschicken", () => void submit(), "blue");
    sendBtn.style.display = "none";

    mount(
      stage,
      h(
        "div",
        { class: "pf-body" },
        h(
          "div",
          { class: "vt-stage" },
          h("p", { class: "muted", style: "font-size:13px;margin:0" }, `${character.label} · Runde ${round} · du bist dran`),
          h("div", { class: "vt-mic-wrap" }, pulse, micBtn),
          status,
          transcriptBox
        )
      )
    );
    clear(foot);
    mount(foot, h("div", { class: "pf-foot-in" }, sendBtn));

    micBtn.onclick = async () => {
      if (recording) {
        await stopRecording();
        return;
      }
      try {
        await recorder.start();
      } catch {
        status.textContent = "Kein Zugriff auf das Mikrofon. Bitte die Berechtigung erlauben.";
        return;
      }
      recording = true;
      micBtn.classList.add("on");
      micBtn.title = "Aufnahme beenden";
      status.textContent = "Läuft – tippe erneut, wenn du fertig bist.";
      sendBtn.style.display = "none";
      transcriptBox.style.display = "none";
      const paint = () => {
        const lvl = recorder.level();
        pulse.style.transform = `scale(${1 + lvl * 0.7})`;
        pulse.style.opacity = String(0.25 + lvl * 0.5);
        raf = requestAnimationFrame(paint);
      };
      paint();
    };

    async function stopRecording(): Promise<void> {
      recording = false;
      cancelAnimationFrame(raf);
      micBtn.classList.remove("on");
      micBtn.disabled = true;
      status.textContent = "Wird erkannt …";
      try {
        const { blob } = await recorder.stop();
        transcript = await transcribe(blob);
        if (!transcript.trim()) {
          status.textContent = "Nichts verstanden – nochmal versuchen.";
          micBtn.disabled = false;
          return;
        }
        transcriptBox.textContent = transcript;
        transcriptBox.style.display = "";
        status.textContent = "Passt das so? Dann abschicken – oder nochmal aufnehmen.";
        sendBtn.style.display = "";
      } catch (err) {
        status.textContent = pbErrorMessage(err);
      } finally {
        micBtn.disabled = false;
        micBtn.title = "Antwort aufnehmen";
      }
    }

    async function submit(): Promise<void> {
      if (!transcript.trim()) return;
      history.push({ role: "user", text: transcript });
      round++;
      await advance();
    }
  }

  /* -------------------------------- Ende ----------------------------------- */

  async function finish(): Promise<void> {
    const partnerTurns = history.filter((t) => t.role === "partner").length;
    const userTurns = history.filter((t) => t.role === "user").length;
    const transcriptText = history.map((t) => `${t.role === "partner" ? character.label : "Ich"}: ${t.text}`).join("\n\n");

    void speechService
      .save({
        topic: topicId,
        mode: "gespraech",
        audience: character.key,
        round,
        seconds: 0,
        transcript: transcriptText,
        rating: {
          contentPct: 0,
          methodPct: 0,
          summary: `Gespräch mit ${character.label}, ${userTurns} Antworten.`,
          criteria: [],
          covered: [],
          missing: [],
          terms: [],
          nextFocus: "",
        },
        content_pct: 0,
        method_pct: 0,
      })
      .catch(() => {});
    markPracticed();

    head.set(1, 0, 1);
    clear(head.right);
    mount(
      stage,
      h(
        "div",
        { class: "pf-body" },
        h(
          "div",
          { class: "pf-setup" },
          h("h1", null, "Gespräch beendet"),
          h("p", { class: "lead" }, `${partnerTurns} Wortmeldungen von ${character.label}, ${userTurns} Antworten von dir.`),
          h("details", { class: "vt-transcript", open: true }, h("summary", null, "Verlauf"), h("p", { style: "white-space:pre-wrap" }, transcriptText))
        )
      )
    );
    mount(
      foot,
      h(
        "div",
        { class: "pf-foot-in" },
        h(
          "div",
          { class: "pf-actions" },
          pfButton("Fertig", toTopic, "ghost"),
          pfBig("Nochmal", () => void startRound())
        )
      )
    );
  }
}
